import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Loader2, MapPin, Users } from 'lucide-react';
import { LeagueScheduledMatch } from '@/hooks/useLeagueMatches';
interface WeekInfo {
  number: number;
  date: string;
  status: 'past' | 'current' | 'future';
  time?: string;
}
interface LeagueMatchCarouselProps {
  matches: LeagueScheduledMatch[];
  weeks: WeekInfo[];
  selectedWeek: number;
  onWeekChange: (weekNumber: number) => void;
  userAttendanceStatus: 'confirmed' | 'not_attending' | 'pending';
  opponentAttendanceStatus: 'confirmed' | 'not_attending' | 'pending';
  isLoading?: boolean;
}
const LeagueMatchCarousel: React.FC<LeagueMatchCarouselProps> = ({
  matches,
  weeks,
  selectedWeek,
  onWeekChange,
  userAttendanceStatus,
  opponentAttendanceStatus,
  isLoading = false
}) => {
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Reset to first match when encounter changes
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [selectedWeek]);

  // Reset if currentMatchIndex is out of bounds
  useEffect(() => {
    if (currentMatchIndex >= matches.length && matches.length > 0) {
      setCurrentMatchIndex(0);
    }
  }, [matches.length, currentMatchIndex]);
  if (isLoading) {
    return <Card className="border-2 border-primary/20">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Cargando partidos...</span>
        </CardContent>
      </Card>;
  }
  if (!matches || matches.length === 0) {
    return <Card className="border-2 border-gray-200">
        <CardContent className="p-6 text-center text-muted-foreground">
          No hay partidos programados para este encuentro
        </CardContent>
      </Card>;
  }
  const currentMatch = matches[currentMatchIndex];
  const totalMatches = matches.length;
  const goToPrevious = () => {
    setCurrentMatchIndex(prev => prev === 0 ? totalMatches - 1 : prev - 1);
  };
  const goToNext = () => {
    setCurrentMatchIndex(prev => prev === totalMatches - 1 ? 0 : prev + 1);
  };

  // Build team strings
  const team1 = currentMatch.team1_player2_name ? `${currentMatch.team1_player1_name} / ${currentMatch.team1_player2_name}` : currentMatch.team1_player1_name;
  const team2 = currentMatch.team2_player2_name ? `${currentMatch.team2_player1_name} / ${currentMatch.team2_player2_name}` : currentMatch.team2_player1_name;
  const matchTime = currentMatch.scheduled_time ? currentMatch.scheduled_time.slice(0, 5) : '';
  const getAttendanceBadge = (status: 'confirmed' | 'not_attending' | 'pending', label: string) => {
    if (status === 'confirmed') {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
          {label}: Confirmado
        </Badge>;
    }
    if (status === 'not_attending') {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
          {label}: No asistirá
        </Badge>;
    }
    return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
        {label}: Pendiente
      </Badge>;
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500 text-white text-xs">Jugado</Badge>;
      case 'in_progress':
        return <Badge className="bg-primary text-primary-foreground text-xs">En curso</Badge>;
      case 'scheduled':
      default:
        return;
    }
  };
  return <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
      <CardHeader className="pb-3">
        <div className="flex flex-col items-center space-y-3">
          <CardTitle className="text-lg font-bold text-foreground">
            Próximos Partidos
          </CardTitle>

          {/* Attendance badges */}
          <div className="flex flex-wrap justify-center gap-2">
            {getAttendanceBadge(userAttendanceStatus, 'Tu asistencia')}
            {getAttendanceBadge(opponentAttendanceStatus, 'Rivales')}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Navigation Header with Court */}
        <div className="flex items-center justify-between mb-4 bg-white rounded-lg p-3 border border-gray-200">
          <Button variant="ghost" size="icon" onClick={goToPrevious} className="h-8 w-8" disabled={totalMatches <= 1}>
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-lg font-bold text-gray-900">
              Cancha {currentMatch.court_number || 1}
            </div>
            
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm font-semibold">
              {currentMatchIndex + 1}/{totalMatches}
            </Badge>
            <Button variant="ghost" size="icon" onClick={goToNext} className="h-8 w-8" disabled={totalMatches <= 1}>
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Teams Display */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {/* Team 1 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="font-medium text-gray-900 text-sm">{team1}</span>
            </div>
            {getStatusBadge(currentMatch.status)}
          </div>
          
          {/* VS divider */}
          <div className="text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
            vs
          </div>
          
          {/* Team 2 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-orange-500 flex-shrink-0" />
              <span className="font-medium text-gray-700 text-sm">{team2}</span>
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        {totalMatches > 1 && <div className="flex justify-center gap-2 mt-4">
            {matches.map((_, idx) => <button key={idx} onClick={() => setCurrentMatchIndex(idx)} className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentMatchIndex ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Ir al partido ${idx + 1}`} />)}
          </div>}

        {/* Summary footer */}
        <div className="text-center text-xs text-muted-foreground pt-3 mt-3 border-t border-gray-100">
          {totalMatches} partidos programados con rotación de parejas
        </div>
      </CardContent>
    </Card>;
};
export default LeagueMatchCarousel;